/**
 * Which replies a held permission request takes, and what a Deny tells the
 * agent, decided once for the card that offers them (`question-card.tsx`,
 * through `approvals.ts`), the transcript that reads a Deny back
 * (`transcript-model.ts`) and the consent guard that enforces and sends them
 * (`consent.ts`). It sits apart from the guard so the card and the
 * transcript, which a plain browser's `/code` loads, do not bring the guard
 * with them: the guard loads with the page's client, only where the desktop
 * plugin or a development pair is (`connection.ts`).
 */
import type { PendingApproval } from './consent';

/**
 * What the person's Deny tells the agent. OpenCode 2.0.26 ends the turn on a
 * bare `reject` and fails the call with a reject's `message` instead, which
 * the agent reads and goes on from, as when the person types a note in
 * OpenCode's own terminal (`packages/core/src/permission.ts`, `reply`). Stop
 * is how the person ends the turn. The transcript reads a call that failed
 * with exactly this note as declined (`transcript-model.ts`).
 */
export const DECLINE_NOTE =
  'The user declined this request. Do not retry it; continue without it, or ask the user how to proceed.';

/** A shell request whose tool call could not be read back: only `reject` is accepted for it. */
export function isCommandUnreadable(approval: Pick<PendingApproval, 'action' | 'command'>) {
  return approval.action === 'shell' && approval.command === null;
}
